import React from 'react';
import { Box, Button, Chip, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Version } from '../../utils/types/ProjectType';
import { formatVersions, formatZDate } from '../../utils/general';
import {
  mainColors,
  secondaryColors,
  accentColors,
  warningColors
} from '../../theme/themeConfig';

export interface ProjectCardProps {
  detailProjectId: string;
  isClient: boolean;
  item: Version;
  onClick: () => void;
  onLoadVersion: (id: string) => void;
}

function getContractProgress(startDate: string, endDate: string): number {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= end) return 100;
  return Math.round(((now - start) / (end - start)) * 100);
}

function VersionStatusChip({ status }: { status: string }) {
  const lower = status?.toLowerCase() ?? '';
  let bg: string = accentColors.ACCENT_6;
  let color: string = accentColors.ACCENT;
  let border: string = accentColors.ACCENT_2;

  if (['active', 'approved'].includes(lower)) {
    bg = '#EBF4FF';
    color = mainColors.MAIN;
    border = mainColors.MAIN_1;
  } else if (['completed', 'converted', 'paid'].includes(lower)) {
    bg = accentColors.ACCENT_6;
    color = accentColors.ACCENT;
    border = accentColors.ACCENT_2;
  } else if (
    ['in progress', 'inprogress', 'pending', 'working lead'].includes(lower)
  ) {
    bg = warningColors.LIGHT_ERROR;
    color = warningColors.ERROR;
    border = warningColors.ERROR_2;
  } else if (['draft', 'created', 'inactive'].includes(lower)) {
    bg = '#F5F6F8';
    color = secondaryColors.SECONDARY;
    border = secondaryColors.SECONDARY_1;
  } else if (['cancelled', 'canceled', 'rejected', 'lost'].includes(lower)) {
    bg = '#FEF2F2';
    color = '#DC2626';
    border = '#FCA5A5';
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '20px',
        color,
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 600,
        gap: '5px',
        px: '10px',
        py: '3px'
      }}
    >
      <i className={`custom-icon icon-${status}`} style={{ fontSize: 11 }} />
      {status}
    </Box>
  );
}

function ProjectCard({
  item,
  onClick,
  detailProjectId,
  onLoadVersion,
  isClient
}: ProjectCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCurrent = item.projectId === detailProjectId;
  const progress = getContractProgress(
    item.projectStartDate,
    item.projectEndDate
  );

  return (
    <Box
      onClick={onClick}
      sx={{
        '&:hover': { boxShadow: '0 2px 12px rgba(0,93,162,0.10)' },
        background: '#fff',
        border: `1px solid ${isCurrent ? mainColors.MAIN_1 : '#E2E4EA'}`,
        borderRadius: '10px',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s',
        width: '100%'
      }}
    >
      {/* Current-version indicator strip — always rendered to keep all cards the same height */}
      <Box
        sx={{
          background: isCurrent ? mainColors.MAIN : 'transparent',
          color: isCurrent ? '#fff' : 'transparent',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          px: 2,
          py: '3px',
          textTransform: 'uppercase',
          userSelect: 'none'
        }}
      >
        Current Version
      </Box>

      <Box sx={{ p: '16px 20px' }}>
        {/* Header row: status chip + version badge */}
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, mb: 1.5 }}>
          <VersionStatusChip status={item.projectStatus} />
          <Box sx={{ ml: 'auto' }}>
            <Chip
              color="secondary"
              label={
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 700, lineHeight: 1 }}
                  variant="button_100"
                >
                  v{formatVersions(item.projectVersion)}
                </Typography>
              }
              size="small"
              sx={{
                background: '#EBF4FF',
                border: `1px solid ${mainColors.MAIN_1}`,
                color: mainColors.MAIN,
                height: 24
              }}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Project name */}
        <Typography
          sx={{
            color: '#0f1c2e',
            fontSize: '13px',
            fontWeight: 700,
            mb: 1.5
          }}
        >
          {item.projectName}
        </Typography>

        {/* Contract period */}
        <Box sx={{ mb: 0.5 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mb: '4px'
            }}
          >
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '11px' }}
            >
              {t('LISTING_PAGES.dateRange')}
            </Typography>
            <Typography
              sx={{
                color: secondaryColors.SECONDARY_5,
                fontSize: '11px',
                fontWeight: 600
              }}
            >
              {`${formatZDate(item.projectStartDate)} → ${formatZDate(
                item.projectEndDate
              )}`}
            </Typography>
          </Box>
          <LinearProgress
            sx={{
              '& .MuiLinearProgress-bar': {
                backgroundColor: isCurrent
                  ? mainColors.MAIN
                  : accentColors.ACCENT,
                borderRadius: '99px'
              },
              backgroundColor: '#E2E4EA',
              borderRadius: '99px',
              height: 4
            }}
            value={progress}
            variant="determinate"
          />
          <Typography
            sx={{
              color: secondaryColors.SECONDARY,
              fontSize: '10px',
              mt: '3px',
              textAlign: 'right'
            }}
          >
            {progress}% complete
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {!isClient && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/app/leads/edit-lead`, {
                  state: { id: item.leadId }
                });
              }}
              size="small"
              sx={{
                '&:hover': {
                  background: mainColors.MAIN_LIGHT,
                  borderColor: mainColors.MAIN_1
                },
                borderColor: '#E2E4EA',
                borderRadius: '6px',
                color: secondaryColors.SECONDARY_5,
                flex: 1,
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'none'
              }}
              variant="outlined"
            >
              Go to lead
            </Button>
          )}
          <Button
            disabled={isCurrent}
            onClick={(e) => {
              e.stopPropagation();
              if (!isCurrent) onLoadVersion(item.projectId);
            }}
            size="small"
            sx={{
              borderRadius: '6px',
              flex: 1,
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'none',
              ...(isCurrent
                ? {
                    background: '#F5F6F8',
                    borderColor: '#E2E4EA',
                    color: secondaryColors.SECONDARY
                  }
                : {})
            }}
            variant={isCurrent ? 'outlined' : 'primary-outlined'}
          >
            {isCurrent ? 'Current Version' : 'Load this version'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

interface Props {
  detailProjectId: string;
  isClient: boolean;
  onLoadVersion: (projectId: string) => void;
  versions: Version[];
}

const VersionsList: React.FC<Props> = function VersionsList({
  detailProjectId,
  versions,
  onLoadVersion,
  isClient
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        px={1}
        sx={{ justifyContent: 'flex-start' }}
      >
        {versions.length > 0 &&
          versions.map((version: any) => (
            <Box
              key={version.id}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                mb: 0.5,
                width: '360px'
              }}
            >
              <ProjectCard
                detailProjectId={detailProjectId}
                isClient={isClient}
                item={version}
                onClick={() => {}}
                onLoadVersion={onLoadVersion}
              />
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default VersionsList;
